import React from 'react'
import ReactDOM from 'react-dom/client'
import posthog from 'posthog-js'
import './i18n'
import App from './App.tsx'
import { scrubPosthogEvent, scrubPosthogLog } from '@/services/core/posthogPrivacy'
import { reportRollbarError } from '@/services/core/rollbar'

const posthogProjectToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST

if (!posthogProjectToken || !posthogHost) {
  if (import.meta.env.DEV) {
    const missingVariable = !posthogProjectToken
      ? 'VITE_PUBLIC_POSTHOG_PROJECT_TOKEN'
      : 'VITE_PUBLIC_POSTHOG_HOST'
    throw new Error(
      `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`
    )
  }
} else {
  posthog.init(posthogProjectToken, {
    api_host: posthogHost,
    defaults: '2026-01-30',
    logs: {
      serviceName: 'rui-web',
      environment: import.meta.env.MODE,
      beforeSend: scrubPosthogLog,
    },
    before_send: scrubPosthogEvent,
    capture_performance: false,
    disable_capture_url_hashes: true,
    enable_recording_console_log: false,
    session_recording: {
      recordHeaders: false,
      recordBody: false,
      // Returning null omits the URL as well as headers and bodies if network
      // capture is ever enabled for this PostHog project.
      maskCapturedNetworkRequestFn: () => null,
    },
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
  })
}

ReactDOM.createRoot(document.getElementById('root')!, {
  onUncaughtError: (error, errorInfo) => {
    reportRollbarError(error, { componentStack: errorInfo.componentStack })
  },
  onCaughtError: (error, errorInfo) => {
    reportRollbarError(error, { componentStack: errorInfo.componentStack })
  },
}).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)

// Hidden synthwave 84 theme — activate via console: goSynth()
;(window as any).goSynth = () => {
  const body = document.body
  const isActive = body.classList.contains('synthwave-theme')
  if (isActive) {
    body.classList.remove('synthwave-theme')
    body.classList.remove('dark-theme')
    const saved = localStorage.getItem('theme')
    if (saved === 'dark') body.classList.add('dark-theme')
    else document.documentElement.classList.remove('dark')
    localStorage.removeItem('synthwave')
    console.log(
      '%c SYNTHWAVE 84 OFF ',
      'background:#241b2f;color:#7b6b9a;font-size:14px;padding:4px 8px;border-radius:4px;'
    )
  } else {
    body.classList.add('dark-theme', 'synthwave-theme')
    document.documentElement.classList.add('dark')
    localStorage.setItem('synthwave', '1')
    console.log(
      '%c⚡ SYNTHWAVE 84 ⚡',
      'background:linear-gradient(90deg,#ff2975,#36f9f6);color:#241b2f;font-weight:bold;font-size:18px;padding:8px 16px;border-radius:4px;text-shadow:none;'
    )
  }
}

// Restore synthwave on reload if it was active
if (localStorage.getItem('synthwave') === '1') {
  document.body.classList.add('dark-theme', 'synthwave-theme')
  document.documentElement.classList.add('dark')
}
